/**
 * Componente PIPOSSelector
 * 
 * Seletor para alternar entre múltiplas TASKS ativas
 * Permite trocar de TASK sem fechar a janela PIP
 * 
 * @author VISION DESK
 * @version 0.24.0
 */

import React, { useState } from 'react';
import { RefreshCw, ChevronDown, Clock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { listActiveSessions, formatDuration } from '@/services/workSessionApi';
import type { PIPOSSelectorProps } from '@/types/pictureInPicture';

export const PIPOSSelector: React.FC<PIPOSSelectorProps> = ({
  currentOSId,
  onSelectOS,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Buscar todas as sessões ativas do usuário
  const { data: activeSessions, isLoading, refetch } = useQuery({
    queryKey: ['active-sessions-pip'],
    queryFn: listActiveSessions,
    refetchInterval: 30000, // Atualizar a cada 30 segundos
  });

  const handleSelectOS = (osId: string, sessionId: string) => {
    if (osId !== currentOSId) {
      onSelectOS(osId, sessionId);
      setIsOpen(false);
    }
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    refetch();
  };

  const activeSessionsList = activeSessions?.sessions || [];
  
  // CORREÇÃO v0.28.8: Mostrar TODAS as sessões (inclusive pausadas)
  // Usuário pode trocar para qualquer sessão, mesmo pausadas
  // Auto-switch prioriza em execução, mas aceita pausadas
  const otherSessions = activeSessionsList.filter(
    (s: any) => s.orderOfServiceId !== currentOSId
  );

  return (
    <div className="space-y-2">
      {/* Botão Principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-3 px-4 bg-purple-500/20 hover:bg-purple-500/30
                 text-purple-300 rounded-lg transition-colors
                 flex items-center justify-between group"
      >
        <div className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5" />
          <span className="font-medium">
            {otherSessions.length > 0
              ? `Trocar TASK (${otherSessions.length} disponível${otherSessions.length > 1 ? 'eis' : ''})`
              : 'Nenhuma outra TASK ativa'}
          </span>
        </div>
        <ChevronDown
          className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown de TASKS Ativas */}
      {isOpen && (
        <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="px-4 py-2 bg-white/5 border-b border-white/10 flex items-center justify-between">
            <span className="text-xs text-gray-400">Outras TASKS Ativas:</span>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              title="Atualizar lista"
            >
              <RefreshCw
                className={`w-3 h-3 text-gray-400 ${isLoading ? 'animate-spin' : ''}`}
              />
            </button>
          </div>

          {/* Lista de TASKS - Altura limitada com scroll interno para não empurrar o footer */}
          <div className="max-h-52 overflow-y-auto custom-scrollbar">
            {isLoading ? (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                Carregando...
              </div>
            ) : otherSessions.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                Nenhuma outra TASK ativa no momento
              </div>
            ) : (
              otherSessions.map((session: any) => (
                <button
                  key={session.id}
                  onClick={() =>
                    handleSelectOS(session.orderOfServiceId, session.id)
                  }
                  className="w-full px-4 py-3 hover:bg-white/10 transition-colors
                           text-left border-b border-white/5 last:border-b-0"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">
                        {session.orderOfService?.title || 'TASK sem título'}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-400">
                          {formatDuration(session.duration || 0)}
                        </span>
                        {session.isPaused && (
                          <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 
                                         text-yellow-400 rounded">
                            Pausada
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
